import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const CONSENT_KEY = 'rwa_cookie_consent';

export type CookieConsent = 'essential' | 'all';

export function getCookieConsent(): CookieConsent | null {
  return localStorage.getItem(CONSENT_KEY) as CookieConsent | null;
}

export default function CookieConsentBanner() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!getCookieConsent()) setVisible(true);
  }, []);

  const choose = (choice: CookieConsent) => {
    localStorage.setItem(CONSENT_KEY, choice);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div style={s.wrap} role="dialog" aria-label={t('cookies.label', 'Cookie preferences')}>
      <div style={s.banner}>
        <div style={s.content}>
          <strong style={s.heading}>{t('cookies.title', 'Cookie notice')}</strong>
          <p style={s.body}>
            {t(
              'cookies.body',
              'We use essential cookies required for secure login and to keep your session active. We do not use advertising or tracking cookies without your explicit consent. Accepting all enables anonymous usage analytics to help us improve the app.',
            )}{' '}
            <a href="/privacy" style={s.link} target="_blank" rel="noopener noreferrer">
              {t('cookies.policy', 'Privacy Policy')}
            </a>
          </p>
        </div>
        <div style={s.actions}>
          <button style={{ ...s.btn, ...s.btnSecondary }} onClick={() => choose('essential')}>
            {t('cookies.essential', 'Essential only')}
          </button>
          <button style={{ ...s.btn, ...s.btnPrimary }} onClick={() => choose('all')}>
            {t('cookies.acceptAll', 'Accept all')}
          </button>
        </div>
      </div>
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  wrap: {
    position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9999,
    padding: '12px 16px 16px',
    pointerEvents: 'none',
  },
  banner: {
    maxWidth: '960px', margin: '0 auto',
    background: '#1D1D1F', color: '#F5F5F7',
    borderRadius: '16px', padding: '18px 24px',
    display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap',
    boxShadow: '0 -2px 32px rgba(0,0,0,0.22)',
    pointerEvents: 'auto',
  },
  content: { flex: 1, minWidth: '220px' },
  heading: { display: 'block', marginBottom: '4px', fontSize: '0.9rem' },
  body: { margin: 0, fontSize: '0.82rem', lineHeight: 1.55, color: '#AEAEB2' },
  link: { color: '#64D2FF', textDecorationLine: 'underline' },
  actions: { display: 'flex', gap: '10px', flexShrink: 0, flexWrap: 'wrap', alignSelf: 'center' },
  btn: {
    padding: '10px 18px', borderRadius: '10px', border: 'none',
    fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
  },
  btnSecondary: { background: '#3A3A3C', color: '#F5F5F7' },
  btnPrimary: { background: '#007AFF', color: '#fff' },
};
